import { getOrganizationId } from "./organizationUtils";

const STORAGE_KEY = "hms_invoice_department_memory_v1";
const MAX_ENTRIES = 500;

const hasText = (value) =>
  typeof value === "string" && value.trim().length > 0;

const normalizeId = (value) => {
  if (value == null || value === "") return null;
  return String(value);
};

const resolveOrganizationId = (organizationId) => {
  if (organizationId != null && organizationId !== "") {
    return String(organizationId);
  }

  try {
    const rawUser = localStorage.getItem("user");
    if (!rawUser) return "0";
    const user = JSON.parse(rawUser);
    return String(getOrganizationId(user) ?? "0");
  } catch (error) {
    return "0";
  }
};

const buildKeys = (organizationId, { invoiceId, appointmentId, patientId }) => {
  const orgId = resolveOrganizationId(organizationId);
  const keys = [];

  const normalizedInvoiceId = normalizeId(invoiceId);
  const normalizedAppointmentId = normalizeId(appointmentId);
  const normalizedPatientId = normalizeId(patientId);

  if (normalizedInvoiceId) keys.push(`${orgId}:invoice:${normalizedInvoiceId}`);
  if (normalizedAppointmentId) keys.push(`${orgId}:appointment:${normalizedAppointmentId}`);
  if (normalizedPatientId) keys.push(`${orgId}:patient:${normalizedPatientId}`);

  return keys;
};

const readMemory = () => {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeMemory = (memory) => {
  try {
    const entries = Object.entries(memory)
      .sort((a, b) => (b[1]?.updatedAt || 0) - (a[1]?.updatedAt || 0))
      .slice(0, MAX_ENTRIES);
    const trimmed = Object.fromEntries(entries);
    const serialized = JSON.stringify(trimmed);
    localStorage.setItem(STORAGE_KEY, serialized);
    sessionStorage.setItem(STORAGE_KEY, serialized);
  } catch (error) {
    // Ignore storage errors to avoid breaking billing flows.
  }
};

export const rememberInvoiceDepartment = (
  organizationId,
  identifiers = {},
  departmentDetails = {}
) => {
  const departmentId = normalizeId(
    departmentDetails.departmentId ?? departmentDetails.department_id
  );
  const departmentName = hasText(
    departmentDetails.departmentName ?? departmentDetails.department_name
  )
    ? (departmentDetails.departmentName ?? departmentDetails.department_name).trim()
    : null;

  if (!departmentId && !departmentName) return;

  const keys = buildKeys(organizationId, identifiers);
  if (!keys.length) return;

  const nextEntry = {
    departmentId,
    departmentName,
    doctorId: normalizeId(departmentDetails.doctorId ?? departmentDetails.doctor_id),
    updatedAt: Date.now(),
  };

  const memory = readMemory();
  keys.forEach((key) => {
    memory[key] = nextEntry;
  });
  writeMemory(memory);
};

export const lookupRememberedDepartment = (organizationId, identifiers = {}) => {
  const memory = readMemory();
  const keys = buildKeys(organizationId, identifiers);
  for (const key of keys) {
    const entry = memory[key];
    if (
      entry &&
      (entry.departmentId ||
        entry.departmentName ||
        entry.doctorId ||
        entry.doctor_id)
    ) {
      return entry;
    }
  }
  return null;
};
